/**
 * @fileoverview Feriados nacionais do Brasil (fixos + móveis derivados da Páscoa).
 * Sem dependências externas. Usado pela visão de Calendário do CRM para destacar
 * os feriados nacionais. Datas locais (não-UTC) para casar com o calendário exibido.
 */

const pad = (n) => String(n).padStart(2, "0");

/** Chave canônica "YYYY-MM-DD" a partir de ano/mês(1-12)/dia. */
const key = (year, month, day) => `${year}-${pad(month)}-${pad(day)}`;

/** Chave canônica "YYYY-MM-DD" a partir de um objeto Date (horário local). */
export const dateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Domingo de Páscoa para o ano informado — algoritmo de Meeus/Jones/Butcher
 * (computus gregoriano). Retorna um Date local.
 */
const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = março, 4 = abril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

/** Date deslocado por `days` em relação a `base` (preserva horário local). */
const addDays = (base, days) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
};

const cache = new Map();

/**
 * Retorna um Map<"YYYY-MM-DD", { name }> com todos os feriados nacionais do ano.
 * Inclui os fixos e os móveis (Carnaval, Sexta-feira Santa, Corpus Christi).
 */
export const getBrazilianHolidays = (year) => {
  if (cache.has(year)) return cache.get(year);

  const map = new Map();

  // Fixos
  map.set(key(year, 1, 1), { name: "Confraternização Universal" });
  map.set(key(year, 4, 21), { name: "Tiradentes" });
  map.set(key(year, 5, 1), { name: "Dia do Trabalho" });
  map.set(key(year, 9, 7), { name: "Independência do Brasil" });
  map.set(key(year, 10, 12), { name: "Nossa Senhora Aparecida" });
  map.set(key(year, 11, 2), { name: "Finados" });
  map.set(key(year, 11, 15), { name: "Proclamação da República" });
  map.set(key(year, 12, 25), { name: "Natal" });

  // Móveis (relativos à Páscoa)
  const easter = easterSunday(year);
  map.set(dateKey(addDays(easter, -48)), { name: "Carnaval" });           // segunda
  map.set(dateKey(addDays(easter, -47)), { name: "Carnaval" });           // terça
  map.set(dateKey(addDays(easter, -2)), { name: "Sexta-feira Santa" });
  map.set(dateKey(addDays(easter, 60)), { name: "Corpus Christi" });

  cache.set(year, map);
  return map;
};

/** Retorna `{ name }` se a data for feriado nacional, senão `null`. */
export const getHoliday = (date) => {
  if (!date) return null;
  const map = getBrazilianHolidays(date.getFullYear());
  return map.get(dateKey(date)) || null;
};
